import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { deployCoversDay, localDateStr } from '@cls/shared';
import { useResetSub } from '../features/filters/useFilters.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { Avatar } from '../components/primitives/Avatar.js';
import { WorkerDetail } from '../components/domain/WorkerDetail.js';
import { ProjectDetail } from '../components/domain/ProjectDetail.js';
import { DeployModal } from '../components/domain/DeployModal.js';
import { DeploymentDetailModal } from '../components/domain/DeploymentDetailModal.js';

interface Worker {
  id: string;
  name: string;
  role: string;
  status: 'available' | 'deployed' | 'unavailable' | 'dnu';
  agencyCode: string | null;
  phone: string;
  companyRef: string | null;
  photo?: string | null;
}

interface Deployment {
  id: string;
  workerId: string;
  workerName: string | null;
  workerPhone: string;
  projectId: string;
  projectName?: string | null;
  status: string;
  startDate: string;
  startTime: string;
  endDate: string | null;
  supervisorName: string | null;
}

interface Project {
  id: string;
  code: string;
  name: string;
  customer: string;
  manager: string | null;
  status: string;
}

interface SignInRecord {
  id: string;
  workerId: string;
  projectId: string;
  type: 'in' | 'out';
  timestamp: string;
}

type View = 'workers' | 'projects';
type CellState = 'free' | 'deployed' | 'on-site' | 'unavailable' | 'sick' | 'holiday';

interface Absence {
  id: string;
  workerId: string;
  type: 'sick' | 'holiday';
  startDate: string;
  endDate: string;
  notes: string | null;
}

const PROJECT_COLOURS = [
  { bg: '#1d4ed8', light: 'rgba(29,78,216,.18)', text: '#93c5fd' },
  { bg: '#7c3aed', light: 'rgba(124,58,237,.18)', text: '#c4b5fd' },
  { bg: '#0891b2', light: 'rgba(8,145,178,.18)', text: '#67e8f9' },
  { bg: '#059669', light: 'rgba(5,150,105,.18)', text: '#6ee7b7' },
  { bg: '#d97706', light: 'rgba(217,119,6,.18)', text: '#fcd34d' },
  { bg: '#dc2626', light: 'rgba(220,38,38,.18)', text: '#fca5a5' },
  { bg: '#0d9488', light: 'rgba(13,148,136,.18)', text: '#5eead4' },
  { bg: '#b45309', light: 'rgba(180,83,9,.18)', text: '#fbbf24' },
  { bg: '#be185d', light: 'rgba(190,24,93,.18)', text: '#f9a8d4' },
  { bg: '#4f46e5', light: 'rgba(79,70,229,.18)', text: '#a5b4fc' },
];

const DOW_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const STATE_ORDER: Record<CellState, number> = { 'on-site': 0, deployed: 1, sick: 2, holiday: 3, free: 4, unavailable: 5 };

interface WorkerDay {
  state: CellState;
  deploy: Deployment | null;
  absence?: Absence | null;
}

interface WorkerTipPayload {
  projectCode: string;
  projectName: string;
  workerName: string;
  workerRole: string;
  date: string;
  startTime: string;
  supervisor: string | null;
}
interface ProjectTipPayload {
  projectCode: string;
  projectName: string;
  onSiteCount: number;
  deployedCount: number;
  manager: string | null;
}
type TipState =
  | null
  | { kind: 'plain'; x: number; y: number; text: string }
  | { kind: 'worker'; x: number; y: number; payload: WorkerTipPayload }
  | { kind: 'project'; x: number; y: number; payload: ProjectTipPayload };

const TIP_WIDTH = 280;
const TIP_HEIGHT = 160;

export function Calendar() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [view, setView] = useState<View>('workers');
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fRole, setFRole] = useState('');
  const [fAgency, setFAgency] = useState('');
  const [fProject, setFProject] = useState('');
  const [tip, setTip] = useState<TipState>(null);

  const [detailWorkerId, setDetailWorkerId] = useState<string | null>(null);
  const [detailProjectId, setDetailProjectId] = useState<string | null>(null);
  const [detailDeploymentId, setDetailDeploymentId] = useState<string | null>(null);
  const [deployModalCtx, setDeployModalCtx] = useState<{ worker: Worker; project: Project } | null>(null);

  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data),
  });
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects').then((r) => r.data),
  });
  const { data: deployments = [] } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data),
  });
  const { data: signIns = [] } = useQuery({
    queryKey: ['signin-records'],
    queryFn: () => api.get<SignInRecord[]>('/signin-records').then((r) => r.data).catch(() => []),
  });
  const { data: absences = [] } = useQuery({
    queryKey: ['absences'],
    queryFn: () => api.get<Absence[]>('/absences').then((r) => r.data).catch(() => [] as Absence[]),
  });

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  useResetSub(() => { setSearch(''); setFStatus(''); setFRole(''); setFAgency(''); setFProject(''); });

  const prev = () => { let y = year, m = month - 1; if (m < 0) { m = 11; y--; } setYear(y); setMonth(m); };
  const nextM = () => { let y = year, m = month + 1; if (m > 11) { m = 0; y++; } setYear(y); setMonth(m); };
  const clearF = () => { setSearch(''); setFStatus(''); setFRole(''); setFAgency(''); setFProject(''); };

  const roleOpts = useMemo(() => [...new Set(workers.map((w) => w.role))].sort(), [workers]);
  const agencyOpts = useMemo(() => [...new Set(workers.map((w) => w.agencyCode).filter(Boolean) as string[])].sort(), [workers]);
  const liveProjects = useMemo(() => projects.filter((p) => p.status === 'live'), [projects]);
  const projectMap = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const pcMap = useMemo(() => {
    const m: Record<string, typeof PROJECT_COLOURS[number]> = {};
    liveProjects.forEach((p, i) => { m[p.id] = PROJECT_COLOURS[i % PROJECT_COLOURS.length]!; });
    return m;
  }, [liveProjects]);
  const projectColour = (pid: string) => pcMap[pid] ?? PROJECT_COLOURS[0]!;

  const dateStrs = useMemo(() => {
    const out: string[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      out.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    return out;
  }, [year, month, daysInMonth]);

  const todayStr = localDateStr(new Date());

  const workerDeploysIdx = useMemo(() => {
    const idx = new Map<string, Deployment[]>();
    for (const d of deployments) {
      if (d.status === 'cancelled') continue;
      const arr = idx.get(d.workerId) ?? [];
      arr.push(d);
      idx.set(d.workerId, arr);
    }
    return idx;
  }, [deployments]);

  const signInsIdx = useMemo(() => {
    const idx = new Set<string>();
    for (const r of signIns) {
      if (r.type !== 'in') continue;
      idx.add(`${r.workerId}|${r.timestamp.slice(0, 10)}`);
    }
    return idx;
  }, [signIns]);

  const absencesIdx = useMemo(() => {
    const idx = new Map<string, Absence[]>();
    for (const a of absences) {
      const arr = idx.get(a.workerId) ?? [];
      arr.push(a);
      idx.set(a.workerId, arr);
    }
    return idx;
  }, [absences]);

  function buildWorkerDay(w: Worker, dateStr: string): WorkerDay {
    if (w.status === 'unavailable' || w.status === 'dnu') return { state: 'unavailable', deploy: null };
    const deploys = (workerDeploysIdx.get(w.id) ?? []).filter((r) => deployCoversDay(r, dateStr, todayStr));
    const filt = fProject ? deploys.filter((r) => r.projectId === fProject) : deploys;
    const onSite = filt.length > 0 && signInsIdx.has(`${w.id}|${dateStr}`);
    // A real sign-in beats a recorded absence; otherwise the absence wins over
    // the underlying deployment so ops can see who is off and when.
    if (!onSite) {
      const absence = (absencesIdx.get(w.id) ?? []).find(
        (a) => a.startDate.slice(0, 10) <= dateStr && a.endDate.slice(0, 10) >= dateStr,
      );
      if (absence) return { state: absence.type, deploy: filt[0] ?? null, absence };
    }
    if (filt.length) {
      return { state: onSite ? 'on-site' : 'deployed', deploy: filt.find((r) => r.status === 'active') ?? filt[0]! };
    }
    return { state: 'free', deploy: null };
  }

  const { user } = useAuth();
  const { isWorker, canDeploy } = useRolePerms();

  // Pre-filtered worker pool (search + role + agency + project membership).
  // A Worker only sees their own row regardless of filters.
  const workerPool = useMemo(() => {
    return workers.filter((w) => {
      if (isWorker && user?.workerId && w.id !== user.workerId) return false;
      const s = search.toLowerCase().trim();
      if (s) {
        const hay = [w.name, w.role, w.companyRef ?? '', w.agencyCode ?? ''].join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (fRole && w.role !== fRole) return false;
      if (fAgency && (w.agencyCode ?? '') !== fAgency) return false;
      if (fProject) {
        const hasOnProj = (workerDeploysIdx.get(w.id) ?? []).some((d) => d.projectId === fProject);
        if (!hasOnProj) return false;
      }
      return true;
    });
  }, [workers, search, fRole, fAgency, fProject, workerDeploysIdx, isWorker, user?.workerId]);

  // Build per-day state matrix once, then filter by status, then sort.
  const workerRows = useMemo(() => {
    const rows = workerPool.map((w) => {
      const days = dateStrs.map((ds) => buildWorkerDay(w, ds));
      return { worker: w, days };
    });
    const filtered = fStatus ? rows.filter((r) => r.days.some((d) => d.state === fStatus)) : rows;
    filtered.sort((a, b) => {
      const as = Math.min(...a.days.map((d) => STATE_ORDER[d.state] ?? 9));
      const bs = Math.min(...b.days.map((d) => STATE_ORDER[d.state] ?? 9));
      if (as !== bs) return as - bs;
      return a.worker.name.localeCompare(b.worker.name);
    });
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerPool, dateStrs, fStatus, workerDeploysIdx, signInsIdx, absencesIdx, fProject]);

  const filteredProjects = useMemo(() => {
    return liveProjects.filter((p) => {
      const s = search.toLowerCase().trim();
      if (s && !p.name.toLowerCase().includes(s) && !p.code.toLowerCase().includes(s) && !(p.customer ?? '').toLowerCase().includes(s)) return false;
      if (fProject && p.id !== fProject) return false;
      return true;
    });
  }, [liveProjects, search, fProject]);

  const rowCount = view === 'workers' ? workerRows.length : filteredProjects.length;

  function placeTip(clientX: number, clientY: number) {
    let x = clientX + 14;
    let y = clientY + 14;
    if (typeof window !== 'undefined') {
      if (x + TIP_WIDTH > window.innerWidth) x = clientX - TIP_WIDTH - 14;
      if (y + TIP_HEIGHT > window.innerHeight) y = clientY - TIP_HEIGHT - 14;
    }
    return { x, y };
  }
  function showWorkerTip(e: React.MouseEvent, payload: WorkerTipPayload) {
    const p = placeTip(e.clientX, e.clientY);
    setTip({ kind: 'worker', x: p.x, y: p.y, payload });
  }
  function showProjectTip(e: React.MouseEvent, payload: ProjectTipPayload) {
    const p = placeTip(e.clientX, e.clientY);
    setTip({ kind: 'project', x: p.x, y: p.y, payload });
  }
  function showPlainTip(e: React.MouseEvent, text: string) {
    const p = placeTip(e.clientX, e.clientY);
    setTip({ kind: 'plain', x: p.x, y: p.y, text });
  }
  const hideTip = () => setTip(null);

  const filterSelectStyle: React.CSSProperties = {
    fontSize: 11, padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)',
  };

  return (
    <div className="page" id="page-calendar">
      <div className="hbar">
        <div>
          <div className="ph">🗓 Worker Availability Calendar</div>
          <div className="ps">Monthly view of workforce availability and deployments</div>
        </div>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={prev}>‹ Prev</button>
          <span id="cal-month-label" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, minWidth: 140, textAlign: 'center' }}>{monthLabel}</span>
          <button className="btn btn-ghost btn-sm" onClick={nextM}>Next ›</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, padding: '10px 12px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 2, background: 'var(--surface)', borderRadius: 7, padding: 2, border: '1px solid var(--border)' }}>
          <button id="cal-view-workers" className={`btn btn-xs ${view === 'workers' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('workers')} style={{ borderRadius: 5 }}>👷 Workers</button>
          <button id="cal-view-projects" className={`btn btn-xs ${view === 'projects' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('projects')} style={{ borderRadius: 5 }}>🏗 Projects</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '4px 9px', gap: 5, minWidth: 180 }}>
          <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
          <input id="cal-search" type="text" placeholder="Search name, project, role…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: 11, width: '100%' }} />
        </div>
        {view === 'workers' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <span style={{ color: 'var(--muted)', fontSize: 10 }}>Status:</span>
              <select id="cal-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={filterSelectStyle}>
                <option value="">All</option>
                <option value="on-site">On Site</option>
                <option value="deployed">Deployed</option>
                <option value="sick">Sick</option>
                <option value="holiday">Holiday</option>
                <option value="free">Available</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <span style={{ color: 'var(--muted)', fontSize: 10 }}>Role:</span>
              <select id="cal-filter-role" value={fRole} onChange={(e) => setFRole(e.target.value)} style={filterSelectStyle}>
                <option value="">All Roles</option>
                {roleOpts.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <span style={{ color: 'var(--muted)', fontSize: 10 }}>Agency:</span>
              <select id="cal-filter-agency" value={fAgency} onChange={(e) => setFAgency(e.target.value)} style={filterSelectStyle}>
                <option value="">All</option>
                {agencyOpts.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
          <span style={{ color: 'var(--muted)', fontSize: 10 }}>Project:</span>
          <select id="cal-filter-project" value={fProject} onChange={(e) => setFProject(e.target.value)} style={filterSelectStyle}>
            <option value="">All Projects</option>
            {liveProjects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
        </div>
        <button className="btn btn-ghost btn-xs" onClick={clearF} title="Clear all filters">✕ Clear</button>
        <span id="cal-row-count" style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 'auto' }}>
          {view === 'workers' ? `${rowCount} worker${rowCount !== 1 ? 's' : ''} shown` : `${rowCount} project${rowCount !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, padding: '8px 10px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
        <LegendChip color="var(--accent)" border="var(--accent)" label="On site (signed in)" />
        <LegendChip color="rgba(29,78,216,.2)" border="#1d4ed8" label="Deployed (project colour)" />
        <LegendChip color="rgba(34,197,94,.22)" border="rgba(34,197,94,.4)" label="Available" />
        <LegendChip color="rgba(245,158,11,.22)" border="#f59e0b" label="Sick" />
        <LegendChip color="rgba(96,165,250,.22)" border="#60a5fa" label="Holiday" />
        <LegendChip color="rgba(239,68,68,.25)" border="rgba(239,68,68,.5)" label="Unavailable" />
        <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 'auto' }}>Hover for details · Click cell → deployment · Click name → profile</span>
      </div>

      {rowCount === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>No results match your filters</div>
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={clearF}>Clear filters</button>
        </div>
      ) : (
        <div id="calendar-body" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)', borderRadius: 8, border: '1px solid var(--border)', position: 'relative' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface)', borderBottom: '2px solid var(--border)' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '5px 10px', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 3, minWidth: view === 'workers' ? 160 : 180, borderRight: '1px solid var(--border)', fontSize: 9 }}>{view === 'workers' ? 'Worker' : 'Project'}</th>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const d = i + 1;
                  const date = new Date(year, month, d);
                  const dow = (date.getDay() + 6) % 7;
                  const isWeekend = dow >= 5;
                  const isToday = date.getTime() === today.getTime();
                  return (
                    <th key={d} style={{ minWidth: 30, textAlign: 'center', padding: '4px 1px', fontSize: 9, opacity: isWeekend ? .75 : 1, background: isToday ? 'rgba(232,164,39,.12)' : undefined }}>
                      <div style={{ fontWeight: 500, color: 'var(--muted)' }}>{DOW_LETTERS[dow]}</div>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: isToday ? 800 : 500, color: isToday ? 'var(--accent)' : 'var(--text)' }}>{d}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {view === 'workers' ? (
                workerRows.map(({ worker: w, days }) => (
                  <tr key={w.id}>
                    <td style={{ whiteSpace: 'nowrap', padding: '5px 9px', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1, minWidth: 160, borderRight: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setDetailWorkerId(w.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Avatar name={w.name} photo={w.photo ?? null} size={24} fontSize={9} />
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>{w.name}</div>
                          <div style={{ fontSize: 9, color: 'var(--muted)' }}>
                            {w.role}
                            {w.agencyCode ? <> · <span style={{ color: 'var(--info)' }}>{w.agencyCode}</span></> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    {days.map((day, i) => {
                      const d = i + 1;
                      const dateStr = dateStrs[i]!;
                      const date = new Date(year, month, d);
                      const dow = (date.getDay() + 6) % 7;
                      const isWeekend = dow >= 5;
                      const isToday = date.getTime() === today.getTime();
                      let bg = '', border = '', color = '', dot = '';
                      let cellBg = '';
                      let onClick: (() => void) | null = null;
                      let tipPayload: WorkerTipPayload | null = null;
                      let plainTip: string | null = null;

                      if (day.state === 'unavailable') {
                        bg = 'rgba(239,68,68,.25)'; border = 'rgba(239,68,68,.5)'; color = '#ef4444'; dot = '✕';
                        cellBg = 'rgba(239,68,68,.04)';
                        plainTip = `${w.name} · Unavailable · ${dateStr}`;
                      } else if ((day.state === 'sick' || day.state === 'holiday') && day.absence) {
                        const sick = day.state === 'sick';
                        bg = sick ? 'rgba(245,158,11,.22)' : 'rgba(96,165,250,.22)';
                        border = sick ? '#f59e0b' : '#60a5fa';
                        color = sick ? '#f59e0b' : '#60a5fa';
                        dot = sick ? 'S' : 'H';
                        const range = `${day.absence.startDate.slice(0, 10)} → ${day.absence.endDate.slice(0, 10)}`;
                        plainTip = `${w.name} · ${sick ? 'Sick' : 'Holiday'} (${range})${day.absence.notes ? ` · ${day.absence.notes}` : ''}`;
                        if (day.deploy) onClick = () => setDetailDeploymentId(day.deploy!.id);
                      } else if (day.state === 'on-site' && day.deploy) {
                        const pc = projectColour(day.deploy.projectId);
                        bg = pc.bg; border = pc.bg; color = '#fff'; dot = '●';
                        cellBg = 'rgba(232,164,39,.06)';
                        const proj = projectMap.get(day.deploy.projectId);
                        tipPayload = {
                          projectCode: proj?.code ?? day.deploy.projectId,
                          projectName: proj?.name ?? day.deploy.projectName ?? '',
                          workerName: w.name,
                          workerRole: w.role,
                          date: dateStr,
                          startTime: day.deploy.startTime,
                          supervisor: day.deploy.supervisorName,
                        };
                        onClick = () => setDetailDeploymentId(day.deploy!.id);
                      } else if (day.state === 'deployed' && day.deploy) {
                        const pc = projectColour(day.deploy.projectId);
                        bg = pc.light; border = pc.bg; color = pc.text; dot = '▶';
                        const proj = projectMap.get(day.deploy.projectId);
                        tipPayload = {
                          projectCode: proj?.code ?? day.deploy.projectId,
                          projectName: proj?.name ?? day.deploy.projectName ?? '',
                          workerName: w.name,
                          workerRole: w.role,
                          date: dateStr,
                          startTime: day.deploy.startTime,
                          supervisor: day.deploy.supervisorName,
                        };
                        onClick = () => setDetailDeploymentId(day.deploy!.id);
                      } else {
                        bg = 'rgba(34,197,94,.22)'; border = 'rgba(34,197,94,.4)'; color = '#22c55e'; dot = '';
                        plainTip = `${w.name} · Available · ${dateStr}`;
                        if (fProject) {
                          const proj = projectMap.get(fProject);
                          if (proj && canDeploy) onClick = () => setDeployModalCtx({ worker: w, project: proj });
                        }
                      }

                      const cellStyle: React.CSSProperties = { textAlign: 'center', padding: '2px 1px', minWidth: 30 };
                      if (isWeekend) cellStyle.opacity = .4;
                      if (cellBg) cellStyle.background = cellBg;
                      if (isToday) cellStyle.background = 'rgba(232,164,39,.08)';

                      const dotStyle: React.CSSProperties = {
                        background: bg,
                        border: `1.5px solid ${border}`,
                        borderRadius: 5,
                        width: 24,
                        height: 24,
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 9,
                        fontWeight: 700,
                        color,
                        cursor: onClick ? 'pointer' : 'default',
                        transition: 'transform .1s, filter .1s',
                      };
                      if (isToday) {
                        dotStyle.outline = '2px solid var(--accent)';
                        dotStyle.outlineOffset = 2;
                      }

                      return (
                        <td key={d} style={cellStyle}>
                          <div
                            style={dotStyle}
                            onClick={onClick ?? undefined}
                            onMouseEnter={(e) => tipPayload ? showWorkerTip(e, tipPayload) : (plainTip ? showPlainTip(e, plainTip) : null)}
                            onMouseMove={(e) => tipPayload ? showWorkerTip(e, tipPayload) : (plainTip ? showPlainTip(e, plainTip) : null)}
                            onMouseLeave={hideTip}
                          >
                            {dot}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                filteredProjects.map((p) => {
                  const pc = projectColour(p.id);
                  return (
                    <tr key={p.id}>
                      <td style={{ whiteSpace: 'nowrap', padding: '5px 9px', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1, minWidth: 180, borderRight: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setDetailProjectId(p.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 32, borderRadius: 3, background: pc.bg, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', fontFamily: 'Syne, sans-serif' }}>{p.code}</div>
                            <div style={{ fontSize: 10, fontWeight: 500 }}>{p.name}</div>
                            <div style={{ fontSize: 9, color: 'var(--muted)' }}>{p.customer ?? ''}</div>
                          </div>
                        </div>
                      </td>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const d = i + 1;
                        const date = new Date(year, month, d);
                        const dow = (date.getDay() + 6) % 7;
                        const isWeekend = dow >= 5;
                        const isToday = date.getTime() === today.getTime();
                        const dateStr = dateStrs[i]!;
                        const deps = deployments.filter((r) => r.projectId === p.id && deployCoversDay(r, dateStr, todayStr));
                        const filtDeps = deps.filter((r) => {
                          const w = workers.find((x) => x.id === r.workerId);
                          if (!w) return true;
                          if (fRole && w.role !== fRole) return false;
                          if (fAgency && (w.agencyCode ?? '') !== fAgency) return false;
                          if (search) {
                            const s = search.toLowerCase();
                            if (!w.name.toLowerCase().includes(s) && !p.name.toLowerCase().includes(s) && !p.code.toLowerCase().includes(s)) return false;
                          }
                          return true;
                        });
                        const onSiteCount = filtDeps.filter((r) => signInsIdx.has(`${r.workerId}|${dateStr}`)).length;
                        const count = filtDeps.length;

                        let bg = 'transparent';
                        let border = 'var(--border)';
                        let color = pc.bg;
                        let display = '';
                        let onClick: (() => void) | null = null;
                        if (count > 0) {
                          if (onSiteCount > 0) {
                            bg = pc.bg; border = pc.bg; color = '#fff';
                            display = String(onSiteCount);
                          } else {
                            bg = pc.light; border = pc.bg; color = pc.text;
                            display = String(count);
                          }
                          onClick = () => setDetailProjectId(p.id);
                        }

                        const cellStyle: React.CSSProperties = { textAlign: 'center', padding: '2px 1px', minWidth: 30 };
                        if (isWeekend) cellStyle.opacity = .4;
                        if (isToday) cellStyle.background = 'rgba(232,164,39,.08)';

                        const dotStyle: React.CSSProperties = {
                          background: bg,
                          border: `1.5px solid ${border}`,
                          borderRadius: 5,
                          width: 24,
                          height: 24,
                          margin: '0 auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 9,
                          fontWeight: 700,
                          color,
                          cursor: onClick ? 'pointer' : 'default',
                        };
                        if (isToday) {
                          dotStyle.outline = '2px solid var(--accent)';
                          dotStyle.outlineOffset = 2;
                        }

                        const payload: ProjectTipPayload = {
                          projectCode: p.code,
                          projectName: p.name,
                          onSiteCount,
                          deployedCount: count - onSiteCount,
                          manager: p.manager,
                        };

                        return (
                          <td key={d} style={cellStyle}>
                            <div
                              style={dotStyle}
                              onClick={onClick ?? undefined}
                              onMouseEnter={(e) => showProjectTip(e, payload)}
                              onMouseMove={(e) => showProjectTip(e, payload)}
                              onMouseLeave={hideTip}
                            >
                              {display}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {tip ? <RichTooltip tip={tip} /> : null}

      {detailWorkerId ? <WorkerDetail workerId={detailWorkerId} onClose={() => setDetailWorkerId(null)} /> : null}
      {detailProjectId ? <ProjectDetail projectId={detailProjectId} onClose={() => setDetailProjectId(null)} /> : null}
      {detailDeploymentId ? <DeploymentDetailModal deploymentId={detailDeploymentId} onClose={() => setDetailDeploymentId(null)} /> : null}
      {deployModalCtx ? (
        <DeployModal
          workerId={deployModalCtx.worker.id}
          workerName={deployModalCtx.worker.name}
          workerPhone={deployModalCtx.worker.phone}
          projectId={deployModalCtx.project.id}
          projectName={deployModalCtx.project.name}
          onClose={() => setDeployModalCtx(null)}
        />
      ) : null}
    </div>
  );
}

function LegendChip({ color, border, label }: { color: string; border: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--muted)' }}>
      <div style={{ width: 14, height: 14, borderRadius: 4, background: color, border: `1.5px solid ${border}` }} />
      <span>{label}</span>
    </div>
  );
}

function RichTooltip({ tip }: { tip: NonNullable<TipState> }) {
  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    left: tip.x,
    top: tip.y,
    background: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '10px 13px',
    boxShadow: '0 8px 32px rgba(0,0,0,.6)',
    minWidth: 200,
    maxWidth: 280,
    zIndex: 9999,
    pointerEvents: 'none',
    fontSize: 11,
    color: 'var(--text)',
  };
  if (tip.kind === 'plain') {
    return <div style={baseStyle}>{tip.text}</div>;
  }
  if (tip.kind === 'worker') {
    const p = tip.payload;
    const dateLabel = new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return (
      <div style={baseStyle}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, color: 'var(--accent)', marginBottom: 5 }}>{p.projectCode}</div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{p.projectName}</div>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 3 }}>{`👷 ${p.workerName} · ${p.workerRole}`}</div>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>{`📅 ${dateLabel}${p.startTime ? ' at ' + p.startTime : ''}`}</div>
        {p.supervisor ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{`👤 Supervisor: ${p.supervisor}`}</div> : null}
        <div style={{ marginTop: 7, fontSize: 9, color: 'var(--faint)', borderTop: '1px solid var(--border)', paddingTop: 5 }}>Click to open deployment record</div>
      </div>
    );
  }
  // project
  const p = tip.payload;
  return (
    <div style={baseStyle}>
      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, color: 'var(--accent)', marginBottom: 4 }}>{p.projectCode}</div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{p.projectName}</div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{p.onSiteCount}</div>
          <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase' }}>On Site</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{p.deployedCount}</div>
          <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase' }}>Deployed</div>
        </div>
      </div>
      {p.manager ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{`👤 Manager: ${p.manager}`}</div> : null}
    </div>
  );
}
